import React from 'react';

interface IconProps {
  className?: string;
  size?: number;
  scale?: number;
}

/**
 * Solenoid Valve Icon - Electric solenoid valve
 */
export const SolenoidValveIcon: React.FC<IconProps> = ({ className = '', size = 24, scale }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 62.88 82.65"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={scale ? { transform: `scale(${scale})` } : undefined}
  >
    <polygon
      points="31.44 41.44 3.85 80.65 59.03 80.65 31.44 41.44"
      stroke="currentColor"
      strokeMiterlimit="10"
      strokeWidth="4"
      fill="none"
    />
    <polygon
      points="31.44 41.22 3.85 2 59.03 2 31.44 41.22"
      stroke="currentColor"
      strokeMiterlimit="10"
      strokeWidth="4"
      fill="none"
    />
  </svg>
);

/**
 * Circulation Pump Icon - Main circulation/recirculation pump
 */
export const CirculationPumpIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    {/* Pump housing */}
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.5" fill="none" />

    {/* Impeller blades */}
    <path
      d="M12 12 L12 6 M12 12 L17 15 M12 12 L7 15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />

    {/* Center hub */}
    <circle cx="12" cy="12" r="2" fill="currentColor" />

    {/* Inlet */}
    <path d="M12 20 V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />

    {/* Outlet */}
    <path d="M20 12 H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/**
 * Heating Element Icon
 */
export const HeaterIcon: React.FC<IconProps> = ({ className = '', size = 24, scale }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 58.67 100.76"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={scale ? { transform: `scale(${scale})` } : undefined}
  >
    <path d="M33.08,33.29l-1.81,3.92-1.96,4.34c-.78,1.72-1.27,3.44-1.59,5.3-.57,3.24-.18,6.41,1.16,9.42,1.49,3.35,4.04,6.31,5.95,9.6,2.6,4.48,3.86,9.12,2.11,14.23-.71,2.07-1.67,3.95-3,5.75.68-3.89.26-7.59-1.19-11.13-.73-1.76-1.57-3.39-2.57-5.03l-3.81-6.24c-3.77-6.3-5.06-12-3.76-19.39-2.46,6.3-2.58,13.25-.48,19.73.83,2.57,1.89,4.89,3.07,7.31,2.69,5.39,3.53,9.33.63,14.84-.1-4.15-1.29-6.78-4.37-9.32l-3.5-2.87c-3.1-2.55-5.02-6.85-5.08-10.81-.36.09-.14.38-.17.62-.55,5.39.08,10.42,2.44,15.24l2.26,4.6,2.98,6.09c1.4,2.88.18,1.61,2.44,3.63,1.81,1.62,3.53,3.16,5.16,4.89.19-.23.1-.37.21-.43.7,1.07,1.52,2.08,2.16,3.17-3.54.12-7.18-.55-10.67-1.52-3.99-1.11-7.66-3.03-10.83-5.7C3.39,88.92.19,82.27,0,75.13c-.11-4.38.74-8.75,2.48-12.8.3-.69.52-1.29,1.15-1.8-.07,1.5-.12,2.87-.3,4.21-.97,7.45,2.58,12.99,7.88,18.08,2.34,2.25,4.82,4.23,7.35,6.19l-4.4-5.66c-3.8-4.89-6.18-9.68-6.69-15.87-.44-5.38.69-11.71,2.08-17.03l2.32-8.89c.67-2.57,1.13-5.06,1.47-7.68,2.12,6.3-.46,11.36.08,17.41-.03.05-.03.12.06.11-.05.1.01.21.02.31,0,.17-.05.38.11.45.45,3.25,2.01,6.36,4.27,8.92-.39-2.06-.81-3.89-1.06-5.84-.38-2.95-.44-5.9-.15-8.82.03-.31.12-.76.12-1.04.06.04.09-.03.09-.07.01-.06,0-.12-.02-.16.83-6.39,3.36-11.55,6.37-17.26,2.77-5.26,4.86-10.69,6.49-16.36l1.77-6.17c.54-1.86,1.16-3.59,2.02-5.37.01,1.14-.11,2.14-.21,3.25-.38,3.97-.17,7.82.59,11.74.86,4.43,1.65,8.31.78,12.82-.37,1.93-.82,3.79-1.61,5.49Z" />
    <path d="M37.13,31.28c.02.06,0,.18.1.13,1.33,4.44,3.86,8.35,6.59,11.95l2.54,3.98c1.08,1.69,1.84,3.45,2.63,5.39.7-2.81.86-5.63.71-8.52l-.21-3.92c-.04-.85-.13-1.74.35-2.53,1.14,3.6,2.46,6.87,3.95,10.18,2.34,5.2,4.47,9.92,4.82,15.74.66,11-4.19,21.59-12.83,28.38-1.64,1.29-3.3,2.44-5.04,3.62-2.31,1.57-4.47,3.19-6.6,5.05-.12.08-.21.04-.13-.07,1.1-2.07,2.28-4.08,3.67-6.03l3.65-5.11c2.33-3.27,4.71-6.75,5.64-10.69.31-1.3.58-2.6.26-3.96-1.73,4.38-3.81,8.31-6.46,12.07-2.49,3.55-4.85,7.08-7.15,10.75.59-3.08,1.67-5.89,3.04-8.7l2.64-5.4c1.24-2.54,2.02-5.14,2.42-7.94.6-4.27-.19-8.43-2.31-12.18s-4.64-6.07-6.59-9.87c-1.78-3.45-2.39-7.25-1.65-11.09.32-1.65.72-3.16,1.36-4.69.55-1.31,1.01-2.66,1.7-3.85l2.39-4.12c.33.49.38.96.51,1.43Z" />
  </svg>
);

/**
 * Grow Light Icon
 */
export const GrowLightIcon: React.FC<IconProps> = ({ className = '', size = 24, scale }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 39.07 65.14"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={scale ? { transform: `scale(${scale})` } : undefined}
  >
    <path d="M23.03,22.16l-6.91.05-.28-2.05c-.19-1.39-1.51-2.44-2.97-2.61-1.73-.2-3.16.98-3.37,2.59s.98,3.3,2.68,3.33l2.71.05,1.92,28.76-6.58.06-.05-4.19c-.03-4.84-1.74-11.51-4.92-15.18C2.08,29.3.02,24.87,0,19.91S1.75,10.14,5.05,6.39C8.42,2.56,13.11.39,18.14.05c7.29-.49,13.99,3.05,17.86,9.17,5,7.89,3.62,17.04-2.33,23.98-3.42,3.99-4.98,9.91-4.92,15.09v3.91s-6.54.08-6.54.08l2.01-28.79,2.6-.1c1.5-.06,2.55-1.45,2.6-2.84.06-1.48-.95-2.79-2.43-3.03-2.04-.33-3.82,1.32-3.85,3.39,0,.41-.07.71-.13,1.27Z" />
    <path d="M20.91,52.24c-.95.08-1.81.06-2.81.05l-1.92-28.79,6.76-.04-2.03,28.78Z" />
    <polygon points="28.9 60.86 10.31 61.03 10.26 57.6 28.87 57.48 28.9 60.86" />
    <path d="M28.81,56.25l-18.53.13-.02-2.76,18.52-.15c.07.92.07,1.72.03,2.78Z" />
    <path d="M24.05,62.1c-.69,1.83-2.48,2.98-4.29,3.04-1.97.06-3.78-.99-4.58-2.92l8.87-.12Z" />
    <path d="M14.75,22.2l-2.18.02c-1.06,0-1.81-.8-1.77-1.8.04-.95.84-1.69,1.92-1.59,1.67.16,2.1,1.85,2.02,3.38Z" />
    <path d="M24.4,22.16c-.03-.67,0-1.39.24-2.09.31-.91,1.26-1.47,2.19-1.29.87.17,1.35.99,1.31,1.76-.05.82-.7,1.55-1.59,1.6l-2.15.02Z" />
  </svg>
);
export const MixerIcon: React.FC<IconProps> = ({ className = '', size = 24, scale }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 151.28 151.28"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={scale ? { transform: `scale(${scale})` } : undefined}
  >
    <path
      d="M88.83,80.36c.47-3.09,3.66-4.95,6.59-3.85.02,0,.04.02.06.02l27.86,10.39c4.59,1.71,8.51,5.42,11.22,11.28,3.87,8.38,3.35,18.21-1.31,24.76-2.16,3.04-5.68,1.22-8.55-1.22l-35.22-30.04c-1.3-1.11-1.94-2.82-1.68-4.51l1.03-6.85Z"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M64.96,84.71c2.45,1.95,2.45,5.65.04,7.63-.02.01-.03.03-.05.04l-22.93,18.93c-3.78,3.12-8.94,4.66-15.38,4.07-9.19-.84-17.45-6.21-20.79-13.52-1.55-3.39,1.78-5.54,5.33-6.79l43.63-15.48c1.61-.57,3.41-.27,4.75.8l5.41,4.32Z"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M73.13,61.86c-2.91,1.14-6.12-.7-6.63-3.78,0-.02,0-.04-.01-.07l-4.93-29.32c-.81-4.83.44-10.08,4.16-15.36,5.32-7.54,14.1-12.01,22.1-11.25,3.72.35,3.9,4.31,3.22,8.01l-8.41,45.52c-.31,1.68-1.47,3.09-3.06,3.71l-6.44,2.53Z"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M56.98,34.82c-17.14,6.37-28.17,24.53-27.36,42.61.34,1.74,1,4.73-1.19,5.45-2.74.72-2.66-3.53-2.81-5.31-.89-19.72,11.2-39.53,29.92-46.48,2.48-.88,3.88,2.72,1.43,3.73h0Z"
      fill="currentColor"
    />
    <path
      d="M56.41,46.42c-9.75,5.53-15.75,16.3-16.53,27.39-.26,2.61-4.13,2.33-3.99-.31.86-12.32,7.63-24.37,18.52-30.54,1.41-.79,3.75-2.68,4.87-.61,1.23,2.67-1.14,2.86-2.87,4.07h0Z"
      fill="currentColor"
    />
    <path
      d="M105.67,112.68c-17.83,13.83-42.63,14.04-60.18-.36"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
    <path
      d="M58.16,106.2c5.33,2.96,11.41,4.55,17.51,4.56,6.1.02,12.18-1.57,17.49-4.55,1.07-.5,2.13-1.44,3.3-1.71,1.96-.34,2.52,2.18,1.33,3.56-.81.7-1.78,1.08-2.67,1.64-12.74,7.3-29.46,6.58-41.58-1.62-1.2-1.37-.65-3.89,1.31-3.56,1.17.26,2.24,1.19,3.31,1.69h0Z"
      fill="currentColor"
    />
    <path
      d="M97.31,31.63c18.09,7.73,29.54,27.37,28.38,46.85-.05,1.1-.99,1.96-2.09,1.91-1.1-.05-1.96-.99-1.91-2.09.42-8.92-1.77-17.94-6.35-25.59-5.09-8.69-13.32-15.18-22.69-18.73-.38-.16-.8-.84-1.04-1.48-.45-.96-.08-1.61.83-2.05,1.6-.84,3.32.69,4.86,1.18h0Z"
      fill="currentColor"
    />
    <path
      d="M97.57,43.3c10.36,6.44,17.07,18.08,17.87,30.2.08,1.1-.74,2.06-1.84,2.15-1.1.08-2.06-.74-2.15-1.84-.73-10.94-6.7-21.31-16.03-27.13-.91-.58-1.92-1.18-2.88-1.72-1.28-.72-1.28-2.47-.72-3.12,1.64-2,4.04.58,5.75,1.47h0Z"
      fill="currentColor"
    />
    <circle
      cx="75.64"
      cy="75.64"
      r="7.07"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

/**
 * pH Sensor Icon
 */
export const PHSensorIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    {/* Probe body */}
    <rect
      x="9"
      y="2"
      width="6"
      height="14"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />

    {/* Sensor tip */}
    <path
      d="M9 16 L12 22 L15 16"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      fill="none"
    />

    {/* pH text */}
    <text x="10" y="10" fontSize="5" fill="currentColor" fontWeight="bold">
      pH
    </text>
  </svg>
);

/**
 * EC Sensor Icon
 */
export const ECSensorIcon: React.FC<IconProps> = ({ className = '', size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    {/* Probe body */}
    <rect
      x="9"
      y="2"
      width="6"
      height="14"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.5"
      fill="none"
    />

    {/* Sensor tip with electrodes */}
    <path
      d="M9 16 L12 22 L15 16"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      fill="none"
    />

    {/* Electrode lines */}
    <path d="M10.5 17 V20" stroke="currentColor" strokeWidth="1" />
    <path d="M13.5 17 V20" stroke="currentColor" strokeWidth="1" />

    {/* EC label */}
    <text x="10" y="10" fontSize="5" fill="currentColor" fontWeight="bold">
      EC
    </text>
  </svg>
);

export const PeristalticPumpIcon = (props: any) => (
  <svg
    id="Peristaltics"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 133.45 136.82"
    fill="currentColor"
    {...props}
  >
    <path
      id="input"
      d="M94.26,124.41c.26,2.48.56,4.96.73,7.44.27,4,.84,4.77,4.8,4.88,4.64.13,9.3.11,13.94-.04,5.4-.18,9.57-2.8,12.75-7.06,2.91-3.91,3.69-8.41,3.69-13.17,0-10.9.01-21.8.05-32.7-5.59,18.02-18.8,32.77-35.95,40.65Z"
    />
    <ellipse
      id="pump_body"
      data-name="pump body"
      cx="66.73"
      cy="65.69"
      rx="64.73"
      ry="63.69"
      fill="none"
      stroke="currentColor"
      strokeMiterlimit="10"
      strokeWidth="4"
    />
    <circle id="center" cx="66.73" cy="65.69" r="12.53" />
    <path
      id="arrow"
      d="M40.57,94.15c7.19,6.6,16.4,10.18,26.1,10.18.56,0,1.12-.01,1.68-.03,10.31-.44,19.83-4.87,26.81-12.47,6.98-7.6,10.59-17.46,10.15-27.77-.42-9.76-4.41-18.81-11.28-25.67l3.22-3.62c.58-.64.24-1.66-.6-1.83l-6.44-1.34-6.78-1.42c-.85-.17-1.56.63-1.29,1.45l4.23,12.82c.26.82,1.32,1.04,1.89.4l3.77-4.22c13.3,13.41,13.83,35.12.92,49.17-6.44,7.01-15.22,11.1-24.73,11.5-9.51.4-18.61-2.92-25.62-9.36-14.47-13.29-15.43-35.88-2.14-50.35l-2.21-2.03c-14.41,15.69-13.37,40.18,2.32,54.59Z"
    />
    <path
      id="output"
      d="M39.2,124.41c-.26,2.48-.56,4.96-.73,7.44-.27,4-.84,4.77-4.8,4.88-4.64.13-9.3.11-13.94-.04-5.4-.18-9.57-2.8-12.75-7.06-2.91-3.91-3.69-8.41-3.69-13.17,0-10.9-.01-21.8-.05-32.7,5.59,18.02,18.8,32.77,35.95,40.65Z"
    />
  </svg>
);

export const PumpIcon = (props: any) => (
  <svg
    id="pump"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 78 78"
    fill="currentColor"
    {...props}
  >
    <circle
      cx="39"
      cy="39"
      r="37.5"
      fill="none"
      stroke="currentColor"
      strokeMiterlimit="10"
      strokeWidth="3"
    />
    <path d="M58.74,10.26l-.12,58.09c0,2.21-2.4,3.6-4.32,2.49L4.15,41.86c-1.92-1.11-1.92-3.88,0-4.99L54.42,7.76c1.92-1.11,4.33.28,4.32,2.5Z" />
  </svg>
);

export const CoolingValveIcon: React.FC<IconProps> = ({ className = '', size = 24, scale }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 68.3 68.85"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={scale ? { transform: `scale(${scale})` } : undefined}
  >
    <path
      d="M8.66,33.77c-.18.26-.57.28-.81.06l-2.23-2.09c.27-.25.55-.4.88-.53l2.74,2.63c-.17.15-.34.21-.59-.07Z"
      fill="currentColor"
    />
    <path
      d="M60.67,33.95c-.32.1-.79.03-1.28-.07l2.68-2.64c.38.11.68.19.97.43l-2.38,2.27Z"
      fill="currentColor"
    />
    <path
      d="M62.92,37.69c-.27.32-.53.33-.85.53l-2.72-2.52c.36-.12,1.02-.27,1.34-.04.81.57,1.4,1.34,2.23,2.04Z"
      fill="currentColor"
    />
    <path
      d="M6.55,38.19c-.39-.09-.67-.22-.96-.44l2.35-2.25,1.27.12-2.66,2.57Z"
      fill="currentColor"
    />
    <path
      d="M15.58,57.5c-.04-1.08-.07-2.06,0-3.1.34-.25.54-.57.92-.73l-.05,3.65c-.27.07-.45.16-.88.18Z"
      fill="currentColor"
    />
    <path
      d="M56.91,16.92h-3.68c.69-1.36,2.23-.71,3.78-.87.05.31-.02.45-.1.87Z"
      fill="currentColor"
    />
    <path
      d="M52.06,15.67l.03-3.53c.22-.18.59-.19.87-.18v2.89c-.27.31-.49.61-.89.82Z"
      fill="currentColor"
    />
    <path
      d="M53.03,57.34c-.27.08-.68.05-.89-.15l-.02-3.42c1.37.69.73,2.05.91,3.57Z"
      fill="currentColor"
    />
    <path
      d="M15.19,16.86l-3.54-.03c-.19-.07-.25-.6-.22-.83,1.49.09,3.23-.55,3.76.86Z"
      fill="currentColor"
    />
    <path
      d="M16.44,15.61c-.38-.07-.6-.44-.9-.76v-2.86c.34-.06.6-.02.88.11l.02,3.52Z"
      fill="currentColor"
    />
    <path
      d="M57.09,53.38c-1.62-.14-3.11.51-3.85-.86,1.24-.04,2.33-.08,3.58.02.23.02.28.57.26.84Z"
      fill="currentColor"
    />
    <path
      d="M14.3,53.36l-2.73.06c-.05-.24-.03-.64.14-.87,1.09-.07,2.21-.07,3.45,0-.12.3-.48.82-.86.82Z"
      fill="currentColor"
    />
    <path
      d="M36.29,65.48l-2.03,2.56-2.02-2.63c.39-.62,1.09-1.02,1.35-1.62.19-.84.01-1.76.03-2.76l-2.35,2.38c-.28-.26-.38-.56-.52-.87l2.9-3.09c-.04-.92.09-1.76-.09-2.61l-3.42,3.47c-.25-.26-.3-.55-.49-.91l3.96-4.14-.05-4.62-3.74,3.8c-.06-.38-.22-1.05-.07-1.44l3.84-4.05-.07-4.21-6.05,6.12v6.34s-1.27,1.07-1.27,1.07l-.06-6.1-3.58,3.52c-.22-.46-.24-.93-.43-1.49l4-4.12-.03-5.88-2.97,2.71-.05,3.96c-.29.21-.63.31-1,.39l-.02-3.23-3.01,2.82-.02,5.87c-.38.12-.63.23-.98.25l-.07-5.07-4.39,4.29-.04,1.99-3.4.36.53-3.24,2.2-.03,4.25-4.2h-4.91c-.01-.37.08-.63.16-1l5.83-.06,2.88-2.94-3.34-.09c.07-.39.16-.69.34-.89l4.11-.18c1.13-.84,1.99-2.03,3.04-3.02h-6.26s-4.19,4.09-4.19,4.09c-.47-.14-.93-.23-1.48-.48l3.65-3.66-6.02-.06c.28-.51.64-.87,1.02-1.29l6.39-.02,6.1-6.01-4.35-.05-4.11,3.96c-.43.14-1.03.01-1.49-.04l3.89-3.88-4.59-.06-4.23,4.03c-.39-.16-.69-.21-.99-.44l3.63-3.57-7.33-.04-1.62,1.42-2.43-2.27,2.69-1.94,1.64,1.59,7.1-.05-3.51-3.46c.26-.24.58-.31.92-.46l4.15,3.91,4.63-.03-3.85-3.79c.48-.17.97-.14,1.46-.08l4.06,3.9,4.35-.08-6.13-6.05h-6.37c-.37-.44-.8-.8-1.08-1.29l6.05-.06-3.58-3.61,1.39-.52,4.15,4.08,6.1-.08c-1.01-.94-1.77-2.15-2.81-2.83l-4.06-.15c-.22-.23-.34-.58-.39-.95l3.39-.06-2.9-3.05-5.92-.03-.29-.94,5.09-.12-4.24-4.25-2.06-.05-.38-3.43,3.26.49v2.1s4.26,4.26,4.26,4.26l.05-4.87c.36-.02.63.03.99.1l.03,5.82,2.98,2.86.03-3.25c.36.04.67.17.95.33.2,1.27-.2,2.76.17,3.98.75,1.09,1.99,1.91,2.9,2.91l.07-6.09-3.95-4.04c.09-.48.24-.92.47-1.45l3.52,3.5.05-5.95c.53.26.82.62,1.28.93v6.32s6.07,6.19,6.07,6.19l.07-4.29-3.92-4.03c-.02-.49-.05-.94.09-1.46l3.79,3.8.06-4.52-4.15-4.31c.13-.32.19-.62.45-.96l3.66,3.65c.07-1.01.05-1.86.02-2.75l-2.96-3.13c.17-.36.25-.63.49-.94l2.43,2.48.05-3.16-1.6-1.7,2.47-2.63,1.96,2.69-1.49,1.67c-.22,1.02.01,2.1-.04,3.19l2.46-2.5c.29.23.39.54.54.86l-3,3.21.04,2.76,3.52-3.53c.26.2.39.54.48.89l-4.04,4.25.08,4.5,3.78-3.77c.03.46.19,1.07.04,1.49l-3.96,4.1.07,4.28,6.15-6.24v-6.27c.43-.38.76-.76,1.29-1.1l.1,6.07,3.57-3.6c.24.54.36.97.47,1.48l-4,4.14.05,6.01c.92-1.01,2.15-1.78,2.85-2.82l.18-4c.26-.17.54-.3.92-.4l.07,3.38,2.95-2.84.03-5.9c.29-.15.57-.25.94-.3l.13,4.99,4.24-4.21c.02-.69-.03-1.31.07-2.03l3.4-.31-.55,3.21-2.17.04-4.29,4.24,5.03.07c.06.36-.01.66-.15.96l-5.9.06-2.84,3,3.45.07c.01.3-.17.6-.27.91l-4.25.18c-1.04.75-1.87,1.93-2.87,2.91l6.15.07,4.22-4.13c.45.07.89.26,1.41.47l-3.63,3.68,6.05.04c-.17.48-.54.78-.83,1.27l-6.59.06-6.12,6,4.34.05,4.11-3.99c.5,0,.94-.05,1.49.09l-3.92,3.89,4.61.05,4.2-4.01c.31-.05.81.19,1.03.36l-3.66,3.64c1.31.09,2.5.01,3.77,0,1.02-.01,2.1.16,3.06-.03.65-.33,1.1-1.05,1.8-1.52l2.49,2.33-2.58,1.87-1.52-1.33-6.96-.04,3.41,3.42c-.25.21-.53.29-.9.44l-4.08-3.82h-4.62s3.79,3.75,3.79,3.75c-.49.13-.91.03-1.45.1l-4.06-3.84-4.23.07,6.07,5.94,6.32.02,1.07,1.27-6.08.06,3.61,3.67c-.5.18-.95.32-1.46.43l-4.16-4.06h-6.25s2.95,3.11,2.95,3.11l4.23.06c.08.32.24.53.29.93l-3.43.06,2.88,2.96,5.92.06c.14.26.25.5.33.96l-5.14.06c.8.89,1.6,1.65,2.48,2.44.61.56,1.11,1.37,1.85,1.76.61.29,1.4-.1,2.04.07l.36,3.41-3.28-.54c-.09-.69-.04-1.31-.05-2.04l-4.26-4.22v4.79c-.34.09-.64,0-.98-.11l-.07-5.75-2.93-2.74-.09,3.11c-.27-.07-.85-.16-.91-.46-.26-1.22.14-2.57-.18-3.78-.75-1.06-1.95-1.85-2.91-2.86l-.04,5.9,4.02,4.19c-.17.55-.22.99-.45,1.49l-3.43-3.46c-.09-.1-.22-.12-.19.04l-.02,5.83c-.62-.26-.9-.64-1.31-.99l-.04-6.35-6.06-6.11-.04,4.28,3.97,4.1c-.06.51.02.92-.09,1.41l-3.78-3.77-.1,4.51,4.1,4.24c-.11.33-.19.63-.39.96l-3.67-3.55c-.04.91-.2,1.88.02,2.66l2.93,3.15c-.11.31-.2.61-.44.89l-2.53-2.46c-.06,1.03-.11,1.94,0,2.89.29.55.94.92,1.34,1.51Z"
      fill="currentColor"
      stroke="currentColor"
      strokeMiterlimit="10"
    />
    <polygon points="35.24 32.27 35.03 27.12 41.01 20.96 41.06 26.56 35.24 32.27" fill="#fff" />
    <polygon points="26.28 41.33 20.7 41.36 26.7 35.46 31.99 35.56 26.28 41.33" fill="#fff" />
    <polygon points="31.78 33.75 26.65 33.89 20.61 28.01 26.2 27.95 31.78 33.75" fill="#fff" />
    <polygon points="33.27 32.13 27.5 26.48 27.54 20.9 33.46 27.01 33.27 32.13" fill="#fff" />
    <polygon points="27.54 48.53 27.5 42.82 33.21 37.28 33.42 42.44 27.54 48.53" fill="#fff" />
    <polygon points="41.95 33.89 36.72 33.8 42.34 28.1 47.99 28.1 41.95 33.89" fill="#fff" />
    <polygon points="40.97 48.41 35.07 42.32 35.23 37.25 40.96 42.78 40.97 48.41" fill="#fff" />
    <polygon points="47.92 41.3 42.27 41.29 36.66 35.61 41.87 35.5 47.92 41.3" fill="#fff" />
  </svg>
);

export const OzonizerIcon: React.FC<IconProps> = ({ className = '', size = 24, scale }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 451.78 406.43"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={scale ? { transform: `scale(${scale})` } : undefined}
  >
    <path d="M315.93,194.2l-44.95-49.5c-24.65,14.62-55.04,15.24-80.04.44l-49.47,54.28c17.93,27.25,17.6,63.67-2.18,89.85-2.93,3.88-6.36,7.52-9.68,10.72-34.01,32.71-88.39,26.55-115.46-12.16C-.03,267.57-4.24,241.8,4.67,216.85c7.2-20.18,23.96-38.75,46.62-46.56,23.35-8.05,50.96-4.89,71.8,10.58l48.05-52.95c-22.88-26.9-24.55-64.58-4.96-94C181.37,11.09,207.86-1.49,235.37.14c26.28,1.56,50.73,16.62,63.41,40.82,15.03,28.66,12.25,59.44-7.93,86.36l31.62,34.81,13.23,14.57c30.95-17.95,70.18-13.53,94.57,12.31,22.41,23.74,27.99,59.08,13.48,88.31-9.07,18.27-25.44,33.35-45.12,39.68-26.6,8.56-52.39,3.3-73.29-14.51-32.1-27.36-36.87-74.88-9.42-108.28ZM300.58,77.89c0-38.73-31.4-70.13-70.13-70.13s-70.13,31.4-70.13,70.13,31.4,70.13,70.13,70.13,70.13-31.4,70.13-70.13ZM285.11,132.92l-8.15,7.56,44.49,47.97,8.15-7.56-44.49-47.97ZM176.47,133.58l-47.45,52.45,7.93,7.18,47.45-52.45-7.93-7.18ZM146.96,243.35c0-38.45-31.17-69.63-69.63-69.63S7.71,204.9,7.71,243.35s31.17,69.63,69.63,69.63,69.63-31.17,69.63-69.63ZM444.21,243.31c0-38.26-31.02-69.28-69.28-69.28s-69.28,31.02-69.28,69.28,31.02,69.28,69.28,69.28,69.28-31.02,69.28-69.28Z" />
    <path d="M296.45,237.67c-.01,20.06-16.28,36.31-36.34,36.3-20.06-.01-36.31-16.28-36.3-36.34.01-20.06,16.28-36.31,36.34-36.3,20.06.01,36.31,16.28,36.3,36.34ZM261,218.63c1.67-.03,3.35-3.67,2.51-4.6-.7-.78-3.11-2.2-4.33-2.13-21.67,1.17-28.26,27.14-21.13,27.31,6.81.17,1.4-20.19,22.95-20.57Z" />
    <path d="M260.13,338.37c0,15.83-12.85,28.66-28.68,28.65-15.83,0-28.66-12.85-28.65-28.68,0-15.83,12.85-28.66,28.68-28.65,15.83,0,28.66,12.85,28.65,28.68ZM235.4,321.3c-.25-1.1-2.31-2.96-3.46-2.96-10.25.02-17.86,7.03-19.55,16.83-.27,1.59,1.44,4.7,2.75,4.53,1.09-.15,3.35-1.97,3.56-3.3,2.52-16.04,18.14-8.78,16.7-15.1Z" />
    <path d="M234.61,177.9c0,12.29-9.97,22.24-22.26,22.23-12.29,0-22.24-9.97-22.23-22.26,0-12.29,9.97-22.24,22.26-22.23,12.29,0,22.24,9.97,22.23,22.26ZM211.79,168.51c.99-.24,2.73-1.73,3.14-2.64s-2.21-2.79-3.37-2.69c-5.63.49-12.46,5.07-12.78,11.75-.06,1.25.65,3.44,1.53,3.9,4.5,2.35,1.16-7.78,11.48-10.33Z" />
    <path d="M209.83,262.94c0,11.31-9.18,20.48-20.5,20.47-11.31,0-20.48-9.18-20.47-20.5,0-11.31,9.18-20.48,20.5-20.47,11.31,0,20.48,9.18,20.47,20.5ZM189.24,253.62c.86-.1,2.84-2.34,2.26-2.91-.43-.43-1.77-1.36-2.37-1.43-5.83-.71-12.7,6.3-12.49,10.8.05,1.04,1.16,2.72,1.59,3.18,2.18,2.33,1.5-8.5,11-9.63Z" />
    <circle cx="226.98" cy="289.83" r="15.12" />
    <circle cx="174.22" cy="312.51" r="15.12" />
    <circle cx="270.12" cy="177.89" r="11.33" />
    <circle cx="212.29" cy="227.75" r="9.9" />
    <circle cx="195.17" cy="396.65" r="9.77" />
  </svg>
);

export default {
  PeristalticPumpIcon,
  SolenoidValveIcon,
  CoolingValveIcon,
  CirculationPumpIcon,
  HeaterIcon,
  GrowLightIcon,
  MixerIcon,
  PHSensorIcon,
  ECSensorIcon,
  OzonizerIcon,
};
